import React, {ReactElement, useState} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  makeStyles
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
// import {ClipboardCopy} from '@components/system/CopyClipBoard/CopyClipBoard';
import QRCode from 'qrcode.react';
import {FileCopy} from '@material-ui/icons';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
// interface for Confirmation Dialog props
interface ShowCopyDialogProps {
  open: boolean;
  title: string | React.ReactNode;
  subtitle: string | React.ReactNode;
  onClose(): void;
  copyText: string;
  downloadQRCodeTitle: string;
  children?: React.ReactNode;
}
const useStyles = makeStyles(theme => ({
  dialogContentContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 0
  },
  qrCodeContainer: {
    justifyContent: 'center'
  },
  copyIcon: {
    paddingRight: '0.4rem'
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  labelContainer: {paddingTop: 0, paddingLeft: '1rem'},
  qrCode: {
    height: 100,
    width: 100,
    marginRight: '2rem'
  },
  subtitle: {fontSize: '1rem'},
  linkStyle: {fontWeight: 600, fontSize: '1.2rem', paddingRight: '1rem'}
}));
const ShowCopyDialog = (props: ShowCopyDialogProps): ReactElement => {
  const {
    open,
    onClose,
    title,
    subtitle,
    copyText,
    downloadQRCodeTitle,
    children
  } = props;
  const classes = useStyles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [isCopied, setIsCopied] = useState(false);
  const handleClose = React.useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);
  async function copyTextToClipboard(text: string) {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }
  const handleCopyClick = () => {
    copyTextToClipboard(copyText)
      .then(() => {
        // If successful, update the isCopied state value
        setIsCopied(true);
        enqueueSnackbar(t('COMMON.COPY_SUCCESS'), SnackBarConfig('i'));
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch(err => {
        throw err;
      });
  };
  // download QR code
  const downloadQRCode = () => {
    const canvas = document.getElementById('qrCodeEl') as HTMLCanvasElement;
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = downloadQRCodeTitle;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };
  return (
    <Dialog
      maxWidth={'md'}
      open={open}
      PaperProps={{style: {minWidth: '500px'}}}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      onClose={handleClose}
      aria-labelledby="copy-link-dialog"
      aria-describedby="copy-link-dialog">
      <DialogTitle disableTypography>{title}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.subtitle}> {subtitle}</div>
        {children}
        <div className={classes.footerContainer}>
          <div>
            <div className={classes.linkStyle}> {copyText}</div>
            <div className="copy-button" data-test-id="pcl-statements-copyLink">
              <IconButton
                id="copy-button-id"
                color="primary"
                aria-label="toggle password visibility"
                onClick={handleCopyClick}
                edge="start"
                className={classes.copyIcon}>
                {<FileCopy color={isCopied ? 'primary' : undefined} />}
              </IconButton>
              <label style={{cursor: 'pointer'}} htmlFor="copy-button-id">
                {t('COMMON.COPY')}
              </label>
            </div>
          </div>
          <div data-test-id="pcl-statements-qrCode">
            <QRCode id="qrCodeEl" value={copyText} className={classes.qrCode} />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <DialogContent className={classes.labelContainer}>
          <div className={classes.dialogContentContainer}>
            <div
              className={classes.qrCodeContainer}
              data-test-id="pcl-statements-downloadQr">
              <Link onClick={downloadQRCode}>
                {t('PAYMENT_HISTORY.EMAIL_DIALOG.DOWNLOAD_QR_CODE')}
              </Link>
            </div>
          </div>
        </DialogContent>

        <Button
          onClick={() => handleClose()}
          color="primary"
          variant="contained">
          {t('SHOW_PAYMENT_URL_DIALOG.CLOSE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowCopyDialog;
